import { Component, OnInit, TrackByFunction, inject, viewChild } from "@angular/core";
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { MutexService } from "common/concurrency/mutex.service";
import { DateTimeInput } from "common/form/input/types/date-time-input";
import { NewDialogComponent } from "common/newdialog/dialog.component";
import { ScrollService } from "common/routing/scroll.service";
import { ProcessReleaseResource } from "core/stages-client";
import { ToDoListDataSource } from "dashboard/to-do-list/to-do-list-data-source.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { DialogHeaderComponent } from "common/newdialog/dialog-header.component";
import { AsyncPipe, NgClass, NgStyle } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { TrimDirective } from "common/trim.directive";
import { AutoGrowDirective } from "common/autoGrow/autogrow.directive";
import { AutofocusDirective } from "common/autofocus/autofocus.directive";
import { InputComponent } from "common/form/input/input.component";
import { DescriptionViewComponent } from "common/editor/description-view.component";
import { ProcessInterfacesInterfacesListComponent } from "management/process-interfaces/interfaces-list/process-interfaces-interfaces-list.component";
import { UserImageComponent } from "common/user-image.component";
import { ButtonsBottomComponent } from "common/buttons/buttons-bottom.component";

@Component({
	selector: "stages-task-details",
	templateUrl: "task-details.component.html",
	styleUrls: ["task-details.component.scss"],
	imports: [
		NewDialogComponent,
		DialogHeaderComponent,
		ReactiveFormsModule,
		NgClass,
		TranslateModule,
		TrimDirective,
		AutoGrowDirective,
		AutofocusDirective,
		InputComponent,
		DescriptionViewComponent,
		ProcessInterfacesInterfacesListComponent,
		UserImageComponent,
		NgStyle,
		ButtonsBottomComponent,
		AsyncPipe,
	],
})
export class TaskDetailsComponent implements OnInit {
	private route = inject(ActivatedRoute);
	private processReleaseResource = inject(ProcessReleaseResource);
	private toDoListDataSource = inject(ToDoListDataSource);
	private scrollService = inject(ScrollService);
	private mutexService = inject(MutexService);

	task$: Observable<stages.process.release.TaskDetails>;
	form!: UntypedFormGroup;
	dateTimeInput?: DateTimeInput;
	newScheduleReleaseInputKey = "newScheduleRelease";

	readonly dialog = viewChild.required<NewDialogComponent>("dialog");

	constructor() {
		const route = this.route;

		this.task$ = route.data.pipe(map((data) => data.task));
	}

	ngOnInit(): void {
		this.task$.subscribe((task) => {
			this.form = new UntypedFormGroup({
				comment: new UntypedFormControl(task.comment, task.commentRequired ? [Validators.required] : []),
			});

			if (task.scheduleProcessReleaseDateAllowed) {
				this.dateTimeInput = new DateTimeInput({
					key: this.newScheduleReleaseInputKey,
					translateKey: "processrelease.workflows.steps.type.RELEASE_PROCESS_VERSION.field.setReleaseDateTime",
					placeholderKey: "management.process.versions.workflows.schedulerelease",
					initialHoursOffset: 1,
					value: task.scheduledDate,
				});

				this.form.addControl(this.newScheduleReleaseInputKey, new UntypedFormControl());
			}
		});
	}

	completeTask(task: stages.process.release.TaskDetails, transitionId: string): void {
		const scheduledProcessReleaseDateControl = this.form.get(this.newScheduleReleaseInputKey);
		const scheduledProcessReleaseDate = scheduledProcessReleaseDateControl?.value
			? new Date(Date.parse(this.form.get(this.newScheduleReleaseInputKey)!.value))
			: null;
		scheduledProcessReleaseDateControl?.setValue(scheduledProcessReleaseDate ? [scheduledProcessReleaseDate] : null);

		this.mutexService.invokeWithResult("completeTask" + task.id, async () => {
			await this.processReleaseResource
				.completeTask(
					this.route.snapshot.paramMap.get("workspaceId")!,
					task.id,
					this.form.get("comment")!.value ?? "",
					{
						transitionId: transitionId,
						scheduledProcessReleaseDate: scheduledProcessReleaseDateControl?.value,
					},
				)
				.then(() => {
					this.toDoListDataSource.dataSource.reloadPage();
					this.close();
				});
		});
	}

	claimTask(task: stages.process.release.TaskDetails): void {
		this.mutexService.invokeWithResult("claimTask" + task.id, async () => {
			await this.processReleaseResource
				.claimTask(this.route.snapshot.paramMap.get("workspaceId")!, task.id)
				.then(() => {
					this.toDoListDataSource.dataSource.reloadPage();
					this.close();
				});
		});
	}

	cancel(): void {
		this.close();
	}

	getStateKey(task: stages.process.release.Task): string {
		if (task.resolution === "EXPIRED") {
			return "dashboard.todolist.task.resolution.EXPIRED";
		}

		return "dashboard.todolist.task.state." + task.state;
	}

	trackBy: TrackByFunction<stages.process.release.Task> = (_index, task) => {
		return task.id;
	};

	private close(): void {
		this.scrollService.setScrollPosition("keep");
		this.dialog().close();
	}
}
